// ─── PULL CONSISTENCY ────────────────────────────────────────────────────────
// Keeps the comp pull a report is built on the same as the pull the analysis
// was built on. Pure functions only, no React and no JSX, so App.js imports
// them and Node can test them (scripts/test-pull-consistency.mjs).
//
// Nothing here reads or stores ARMLS data. A pull fingerprint is counts and
// settings; the dates are the app's own (an analysis message's timestamp, the
// day a report is made). See normalizePullFingerprint.

// The comp buckets, in display order. One copy: App.js imports it from here.
export const COMP_BUCKET_ORDER = ['closed', 'pending', 'active', 'ucb', 'ccbs', 'coming_soon']

// ─── PULL FINGERPRINT ────────────────────────────────────────────────────────
// What a comp pull WAS, as counts and settings only: per-bucket box and
// in-radius counts, the total, tier, radius, and the widen settings the pull
// ran with. No listing ids, no addresses, no prices — nothing that is comp data
// under the ARMLS retention rule. Written on the analysis's message row by
// strategy-chat (sources_used.pull_fingerprint), and compared against the live
// pull before a report is generated.
//
// normalizePullFingerprint is the one shape both sides are compared in, so a
// stored fingerprint and a freshly built one match regardless of key order or
// extra keys. strategy-chat rebuilds the same shape server-side.
export function normalizePullFingerprint(fp) {
  if (!fp || typeof fp !== 'object' || Array.isArray(fp)) return null
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const bool = (v) => (typeof v === 'boolean' ? v : null)
  const counts = {}
  for (const b of COMP_BUCKET_ORDER) {
    const c = fp.counts?.[b]
    counts[b] = c && typeof c === 'object' ? { box_count: num(c.box_count), in_radius: num(c.in_radius) } : null
  }
  const w = fp.widen && typeof fp.widen === 'object' ? fp.widen : {}
  return {
    v: 1,
    counts,
    total_comps: num(fp.total_comps),
    tier: num(fp.tier),
    radius_miles: num(fp.radius_miles),
    widen: {
      subtype_match: bool(w.subtype_match),
      story_match: bool(w.story_match),
      closed_window_months: num(w.closed_window_months),
      year_delta: num(w.year_delta),
    },
  }
}

// The live pull's fingerprint. `overrides` is the tier_overrides object the
// pull was requested with (null for defaults): the widen settings beyond tier
// and radius are not echoed in _meta, so they come from the request.
export function compPullFingerprint(compResult, overrides) {
  const meta = compResult?._meta
  if (!meta || typeof meta !== 'object') return null
  const counts = {}
  for (const b of COMP_BUCKET_ORDER) counts[b] = meta.per_bucket?.[b] || null
  return normalizePullFingerprint({
    counts,
    total_comps: meta.total_comps,
    tier: meta.tier,
    radius_miles: meta.radius_miles,
    widen: overrides || {},
  })
}

export function samePullFingerprint(a, b) {
  const na = normalizePullFingerprint(a)
  const nb = normalizePullFingerprint(b)
  return !!na && !!nb && JSON.stringify(na) === JSON.stringify(nb)
}

// ─── THE ANALYSIS A PULL IS HELD TO ──────────────────────────────────────────
// The most recent assistant message that recorded a pull fingerprint, as
// { fingerprint, createdAt }, or null. Only the "Analyze these comps" turn
// carries one (strategy-chat), so this is the latest analysis of a live pull.
// Analyses made before fingerprints existed, and replies built only on
// uploaded documents, have none and are left alone everywhere below.
export function latestPullAnalysis(messages) {
  if (!Array.isArray(messages)) return null
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    const fp = m?.role === 'assistant' ? m?.sources_used?.pull_fingerprint : null
    if (fp) return { fingerprint: fp, createdAt: m.created_at || null }
  }
  return null
}

// True when the latest analysis recorded a fingerprint and the pull on screen
// does not match it. Generate Report is stopped while this is true. False with
// no pull on screen, and false when no analysis recorded a fingerprint, which
// is exactly what the app did before this check existed.
export function pullDiffersFromAnalysis(messages, compResult, overrides) {
  const latest = latestPullAnalysis(messages)
  if (!compResult || !latest) return false
  return !samePullFingerprint(latest.fingerprint, compPullFingerprint(compResult, overrides))
}

// ─── WIDEN SETTINGS ──────────────────────────────────────────────────────────
// The six widen pills, as the tier_overrides object armls-comp-pull takes.
// null when every pill is off, which is the tight default. The one mapping:
// the Pull comps button and "Re-pull with these" both send this.
export function tierOverridesFromToggles(t) {
  const ov = {}
  if (t?.dropStory) ov.story_match = false
  if (t?.biggerSqft) ov.tier = 2
  if (t?.radius15) ov.radius_miles = 1.5
  if (t?.radius2) ov.radius_miles = 2   // mutually exclusive in the UI, so at most one fires; order kept as a fallback
  if (t?.closings6) ov.closed_window_months = 6
  if (t?.widenYear) ov.year_delta = 15
  return Object.keys(ov).length ? ov : null
}

// The reverse: the pills that reproduce a fingerprint's settings, or null when
// there is no fingerprint. Every setting the Pull comps call can send is in the
// fingerprint — tier and radius as the pull ran them (_meta), the rest as they
// were requested (widen) — so a pull made with these pills fingerprints the
// same settings. Values no pill can produce (a radius of 3, say) map to the
// pill being off; the counts then differ and Generate stops, as it should.
export function widenTogglesFromFingerprint(fp) {
  const n = normalizePullFingerprint(fp)
  if (!n) return null
  return {
    dropStory: n.widen.story_match === false,
    biggerSqft: n.tier === 2,
    radius15: n.radius_miles === 1.5,
    closings6: n.widen.closed_window_months === 6,
    widenYear: n.widen.year_delta === 15,
    radius2: n.radius_miles === 2,
  }
}

// ─── ARIZONA DATES ───────────────────────────────────────────────────────────
// The calendar date in Arizona, as YYYY-MM-DD. Arizona keeps MST all year, so
// this is UTC-7 every day; the time zone name is used rather than the offset
// so the rule reads as what it is. en-CA formats dates as YYYY-MM-DD.
export function arizonaDateISO(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d)
}

// ─── REPORT FILE NAMES ───────────────────────────────────────────────────────
// One saved report per type AND language. The language rides in the name
// (_EN / _ES) because property_case_files has no language column; the date is
// the Arizona date the report was made, not the UTC one.
export function reportLanguageTag(language) {
  return language === 'es' ? 'ES' : 'EN'
}

export function strategyReportFileName(slug, version, language, date = new Date()) {
  return `${slug}_Strategy-Report_Client_${version}_${arizonaDateISO(date)}_${reportLanguageTag(language)}.pdf`
}

// The language a saved report's name carries, or null for a name from before
// the tag existed (and for anything that is not a report name).
export function reportLanguageOfFileName(fileName) {
  const m = typeof fileName === 'string' ? fileName.match(/_(EN|ES)\.pdf$/) : null
  return m ? m[1] : null
}
